/**
 * Absolute-size floors below which the customer chat declines to explain an impact
 * by product (PLT-5904).
 *
 * Shared by the chat's MCP tools, which withhold the breakdown or pathway
 * (`impact-magnitude-gate.ts`), and the net-impact drill-in panel, which offers
 * "why" suggestions only for questions chat would answer (PLT-8016). One definition
 * keeps the two from drifting: a floor tuned on one side only would bring back
 * suggestions that chat then declines.
 *
 * Both floors are on ABSOLUTE size in cents-per-dollar, never on share. The values
 * are in the unit `legacyRelScoreToCentsPerDollar` produces, which is also what a
 * `ScoreReader` reads in `FinalImpactScoreType.CENTS_PER_DOLLAR` with no value set.
 */

/**
 * Floor for one side (positive or negative) of one impact category, in ¢/$. Below
 * this the chat states the size and declines to break the side down into products.
 *
 * Signed off by Jonas at 0.3 (down from a proposed 0.5) — at 0.5 a user might still
 * legitimately be curious what drives the impact.
 */
export const MIN_CATEGORY_SIDE_CENTS_PER_DOLLAR = 0.3;

/**
 * Floor for a single product→impact link, in ¢/$. Applies even inside a category
 * side that clears `MIN_CATEGORY_SIDE_CENTS_PER_DOLLAR`: a material side can still
 * have a long tail of links too small to explain.
 */
export const MIN_PRODUCT_IMPACT_LINK_CENTS_PER_DOLLAR = 0.1;

/**
 * Which revenue a product→impact link's ¢/$ magnitude is measured against.
 *
 * The two customer routes into an impact pathway have different denominators, and the
 * refusal sentence has to say which one it used — otherwise the two numbers read as a
 * contradiction when both turn up in the same conversation.
 *
 * - `company-weighted` — the product's REVENUE-WEIGHTED contribution at company level.
 *   A 1%-of-revenue product with a 20 ¢/$ own score contributes 0.2 ¢/$, and it is the
 *   company-level figure that decides whether that company's link is worth a story.
 *   Deliberately NOT the `categoryScore` column on `get_contributing_products`, which
 *   is the product's own unweighted intensity.
 * - `product-own` — the taxonomy product's own unweighted intensity, i.e. the figure
 *   the product page's Net impact profile shows. The only magnitude that exists when
 *   the question names a product and no company at all.
 */
export type ImpactLinkBasis = 'company-weighted' | 'product-own';

/**
 * The floor a link is judged against, which differs by basis because the two
 * magnitudes are different quantities rather than the same quantity measured twice.
 *
 * A company-weighted contribution is one product's slice of one company, so it gets
 * the link floor — a material category side can still have a long tail of slices too
 * small to explain. A product's own intensity is not a slice of anything: it is the
 * whole of what Upright's analysis says that product does per dollar of its own
 * revenue, which is the same quantity a company's category side measures. So it is
 * judged against the category-side floor — 0.2 ¢/$ of a product's own revenue is as
 * far from breakdown-worthy as a 0.2 ¢/$ category side is, and applying the smaller
 * link floor there would let exactly the reported Covid-19-vaccines case through.
 */
export function minLinkCentsPerDollarForBasis(basis: ImpactLinkBasis): number {
  return basis === 'company-weighted' ? MIN_PRODUCT_IMPACT_LINK_CENTS_PER_DOLLAR : MIN_CATEGORY_SIDE_CENTS_PER_DOLLAR;
}

/**
 * Whether one category side is too small for chat to break down by product. Judged
 * on magnitude, so a side reported as a negative number is gated like its absolute
 * value.
 */
export function isCategorySideTooSmall(centsPerDollar: number): boolean {
  return Math.abs(centsPerDollar) < MIN_CATEGORY_SIDE_CENTS_PER_DOLLAR;
}

/** Whether one product→impact link is too small for chat to give its impact pathway. */
export function isImpactLinkTooSmall(centsPerDollar: number, basis: ImpactLinkBasis): boolean {
  return Math.abs(centsPerDollar) < minLinkCentsPerDollarForBasis(basis);
}
